import EventEmitter from 'eventemitter3';
import { IRoom, ValidateRule, loggerManager, LocalMixVideoTrack, LocalVideoTrack, LocalScreenTrack, ErrorCode as CoreErrorCode, AudioPlayer, getNetworkTimeOffset, showAutoPlayDialog, VideoPlayer, RemoteAudioPlayer } from '.';
import INNER_EVENT, { InnerEventTypes } from './inner-emitter/inner-event';
import innerEmitter from './inner-emitter/inner-emitter';
import * as utils from './utils/index';
import * as constants from './common/constants';
import * as environment from './utils/environment';
import * as fastrx from 'fastrx';
import * as Enums from './enums';
import * as schedule from './utils/schedule';
import eventLogger from './utils/log/event-log';
import { RandContext } from './utils/decorators/random-call';
import * as rtcDectection from './common/rtc-detection';
import kvStatManager, { KVStatManager } from './manager/kv-stat-manager';
import fileDownloader, { FileDownloader } from './utils/file-downloader';
import { startGetPCM } from './manager/dump';
// @ts-ignore
import { ErrorCode, ErrorCodeDictionary } from '../../api-cloud-next/src/common/error-code';
// @ts-ignore
import RtcError from '../../api-cloud-next/src/common/rtc-error';
// @ts-ignore
import TRTCClass from '../../api-cloud-next/src/main';
import { createAudioNode } from './manager/audio-mixer';
import Logger from './utils/log/logger';
import type { VisionTaskType, VisionTaskRegistry } from './manager/vision';
import { getAudioContext } from './utils/audio-context';
import Timer from './utils/timer';
import { DeviceDetector, deviceDetector, getDevices } from './common/device-detector';
// @ts-ignore
import { createScriptTransformWorker } from '../../sdk-webrtc/src/script-transform/script-transform';
import AVPlayerStateSyncManager from './common/player/av-player-state-sync-manager';
import { PlayerEvent } from './common/event';

let isLoaded = false;
let visionPromise: Promise<unknown>;

declare global {
  interface Window {
    VisionTaskType: typeof VisionTaskType;
    VisionTaskRegistry: typeof VisionTaskRegistry;
  }
}

interface ErrorModule {
  RtcError: typeof RtcError;
  ErrorCode: typeof ErrorCode;
  CoreErrorCode: typeof CoreErrorCode;
  ErrorCodeDictionary: typeof ErrorCodeDictionary;
}

export { createDecorator } from './utils/decorators/middleware';
export interface Core {
  TRTC: typeof TRTCClass;
  LocalMixVideoTrack: typeof LocalMixVideoTrack;
  LocalVideoTrack: typeof LocalVideoTrack;
  LocalScreenTrack: typeof LocalScreenTrack;
  trtc: TRTCClass;
  room: IRoom;
  assetsPath?: string;
  preload?: boolean;
  innerEmitter: EventEmitter<InnerEventTypes>;
  INNER_EVENT: typeof INNER_EVENT;
  constants: typeof constants;
  environment: typeof environment;
  utils: typeof utils;
  log: Logger;
  rtcDectection: typeof rtcDectection;
  loggerManager: typeof loggerManager;
  eventLogger: typeof eventLogger;
  errorModule: ErrorModule;
  clearStarted(plugin: IPlugin, group: string): void;
  kvStatManager: KVStatManager;
  fileDownloader: FileDownloader;
  startGetPCM: typeof startGetPCM;
  createAudioNode: typeof createAudioNode;
  rx: typeof fastrx;
  enums: typeof Enums;
  schedule: typeof schedule;
  initVisionTaskRegistry(path: string, preloadModels: VisionTaskType[], visionTaskPath?: string): Promise<void>;
  getDevices: typeof getDevices;
  AudioPlayer: typeof AudioPlayer;
  RemoteAudioPlayer: typeof RemoteAudioPlayer;
  VideoPlayer: typeof VideoPlayer;
  showAutoPlayDialog: typeof showAutoPlayDialog;
  Timer: typeof Timer;
  audioContext: AudioContext;
  deviceDetector: DeviceDetector | null;
  getNetworkTimeOffset: typeof getNetworkTimeOffset;
  validateSourceNode: () => void;
  createScriptTransformWorker: typeof createScriptTransformWorker;
  AVPlayerStateSyncManager: typeof AVPlayerStateSyncManager,
  PlayerEvent: typeof PlayerEvent
}

export interface IPlugin {
  disableRandomCall?: boolean;
  getName(): string;
  getAlias(): string;
  getGroup(option: any): string;
  getValidateRule(method: 'start' | 'update' | 'stop'): ValidateRule | ValidateRule[];
  afterStart?(option: any): void;
  start(option: any): void;
  update(option: any): void;
  stop(option: any): void;
  destroy?(): void;
}

export interface IPluginClass {
  Name: string;
  new(core: Core): IPlugin;
  autoStart?: boolean;
  isSupported?(): boolean;
}

export function createCore(this: TRTCClass, { TRTC, room, errorModule, assetsPath }: { TRTC: typeof TRTCClass, room: IRoom, errorModule: ErrorModule, assetsPath?: string; }): Core {
  return {
    TRTC,
    LocalMixVideoTrack,
    LocalVideoTrack,
    LocalScreenTrack,
    room,
    assetsPath,
    fileDownloader,
    innerEmitter,
    INNER_EVENT,
    constants,
    environment,
    utils,
    eventLogger,
    log: this.room.getLogger(),
    loggerManager,
    errorModule,
    kvStatManager,
    rtcDectection,
    trtc: this,
    rx: fastrx,
    enums: Enums,
    schedule,
    getDevices,
    initVisionTaskRegistry: async (path: string, preloadModels: VisionTaskType[], visionTaskPath = '/mediapipe/vision.js'): Promise<void> => {
      if (!window.VisionTaskRegistry && !isLoaded) {
        isLoaded = true;
        visionPromise = fileDownloader.loadScript(`${path}/${visionTaskPath}`.replace(/([^:]\/)\/+/g, '$1'));// 去掉重复的/
      }
      await visionPromise;
      const instance = await window.VisionTaskRegistry.getInstance(path);
      await instance.preloadModels(preloadModels);
    },
    audioContext: getAudioContext(),
    deviceDetector,
    AudioPlayer,
    RemoteAudioPlayer,
    VideoPlayer,
    showAutoPlayDialog,
    Timer,
    clearStarted: (plugin: IPlugin, group: string) => {
      const prefix = plugin.getAlias();
      const groups = RandContext.instances.get(this);
      if (!groups) return;
      if (group) {
        const ctx = groups.get(prefix + group);
        if (!ctx) return;
        ctx.started = false;
      } else {
        groups.forEach((ctx, group) => {
          if (group.startsWith(prefix)) ctx.started = false;
        });
      }
    },
    startGetPCM,
    createAudioNode,
    getNetworkTimeOffset,
    validateSourceNode: () => {
      if (environment.IS_FIREFOX && !this.room.audioManager._localAudioPipline?.source.node) {
        throw new RtcError({
          code: ErrorCode.DEVICE_ERROR,
          extraCode: ErrorCodeDictionary.NOT_SUPPORTED_MISMATCH_SAMPLE_RATE_IN_FIREFOX,
          message: 'The audio processing plugin cannot be used due to the microphone\'s sampling rate is not 48KHz in Firefox. Please switch to another browser such as Chrome.'
        });
      }
    },
    createScriptTransformWorker,
    AVPlayerStateSyncManager,
    PlayerEvent
  };
}
